import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { X, Upload } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section dédiée au « Conformateur d'oreille ».
 *
 * Règles métier (validées par le client 06/02/2026) :
 * - Matière : silicone uniquement (transparent, non modifiable)
 * - Dureté  : 25 / 35 / 40 / 60 SH
 * - Aucun choix de forme, couleur, marque, écouteur, montage, marquage
 * - Description libre du besoin (obligatoire, min 10 caractères)
 * - Upload de plusieurs photos illustratives (JPG/PNG/WEBP, 8 Mo max/photo)
 * - Tarif forfaitaire : 43 € HT (défini côté backend catalog)
 */
export default function FormConformateurSection({ form, compact = false }) {
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({}); // { file_id: objectURL }

  const sectionClass = compact
    ? 'bg-white border border-zinc-200 p-3 sm:p-4 rounded-sm'
    : 'bg-white border border-zinc-200 p-4 sm:p-6';
  const headingClass = compact
    ? 'font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200'
    : 'font-heading font-semibold text-base sm:text-lg mb-4 pb-2 border-b border-zinc-200';
  const labelClass = compact ? 'label-overline mb-1 block' : 'label-overline mb-2 block';
  const H = compact ? 'h3' : 'h2';

  // Charge les previews des photos déjà uploadées (édition ou brouillon).
  // Les hooks doivent être appelés inconditionnellement — on court-circuite
  // l'effet lorsque la section n'est pas active.
  useEffect(() => {
    if (form.fabricationType !== 'conformateur_oreille') return;
    let alive = true;
    const loadAll = async () => {
      const map = {};
      for (const fid of form.conformateurPhotoIds || []) {
        if (previews[fid]) { map[fid] = previews[fid]; continue; }
        try {
          const res = await axios.get(`${API}/orders/conformateur-photo/${fid}`, { responseType: 'blob' });
          if (!alive) return;
          map[fid] = URL.createObjectURL(res.data);
        } catch {
          if (alive) map[fid] = null;
        }
      }
      if (alive) setPreviews(map);
    };
    loadAll();
    return () => { alive = false; };
  }, [form.fabricationType, form.conformateurPhotoIds]);

  if (form.fabricationType !== 'conformateur_oreille') return null;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    setUploading(true);
    const newIds = [];
    try {
      for (const file of files) {
        if (!allowed.includes(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
          toast.error(`Format non supporté : ${file.name} (JPG, PNG ou WEBP requis)`);
          continue;
        }
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`Photo trop volumineuse : ${file.name} (8 Mo max)`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        try {
          const res = await axios.post(`${API}/orders/upload-conformateur-photo`, fd);
          newIds.push(res.data.file_id);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Erreur upload ${file.name}`);
        }
      }
      if (newIds.length) {
        form.setConformateurPhotoIds([...(form.conformateurPhotoIds || []), ...newIds]);
        toast.success(`${newIds.length} photo(s) ajoutée(s)`);
      }
    } finally {
      setUploading(false);
      // Reset input pour permettre de re-sélectionner le même fichier au besoin
      e.target.value = '';
    }
  };

  const removePhoto = (fid) => {
    form.setConformateurPhotoIds((form.conformateurPhotoIds || []).filter(x => x !== fid));
    setPreviews((prev) => {
      const cp = { ...prev };
      if (cp[fid]) URL.revokeObjectURL(cp[fid]);
      delete cp[fid];
      return cp;
    });
  };

  const descLen = (form.conformateurDescription || '').trim().length;
  const descOK = descLen >= 10;

  return (
    <div className={sectionClass} data-testid="section-conformateur">
      <H className={headingClass}>Conformateur d&apos;oreille</H>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4">
        <p className="text-xs sm:text-sm text-blue-800">
          <strong>Tarif forfaitaire : 43 € HT.</strong> Silicone transparent uniquement.
          Renseignez la dureté souhaitée, votre besoin, et joignez éventuellement des photos.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className={labelClass}>Matière</Label>
          <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-sm text-sm text-zinc-700" data-testid="conformateur-matiere-fixed">
            Silicone transparent
          </div>
        </div>
        <div>
          <Label className={labelClass}>Dureté (Shore) *</Label>
          <Select value={form.durete} onValueChange={form.setDurete}>
            <SelectTrigger className="rounded-sm bg-white" data-testid="conformateur-durete-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {/* Silicone transparent : disponible en 25 / 40 / 60 SH (pas de transparent en 35 SH AQUAPLUS) */}
              <SelectItem value="25">25 SH (Très souple)</SelectItem>
              <SelectItem value="40">40 SH (Standard)</SelectItem>
              <SelectItem value="60">60 SH (Ferme)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-4">
        <Label className={labelClass}>Description du besoin * <span className="text-xs text-zinc-500 font-normal">(min. 10 caractères)</span></Label>
        <textarea
          value={form.conformateurDescription}
          onChange={(e) => form.setConformateurDescription(e.target.value)}
          placeholder="Décrivez précisément l'usage attendu, la morphologie ou toute contrainte à connaître pour la fabrication du conformateur..."
          className={`w-full p-3 border rounded-sm resize-none h-28 focus:outline-none focus:ring-2 focus:ring-zinc-900 text-sm ${descOK ? 'border-zinc-200' : 'border-amber-400'}`}
          data-testid="conformateur-description-textarea"
        />
        <p className={`text-xs mt-1 ${descOK ? 'text-zinc-500' : 'text-amber-700'}`}>
          {descLen} caractère{descLen > 1 ? 's' : ''} — {descOK ? 'OK' : `il en manque ${Math.max(10 - descLen, 0)}`}
        </p>
      </div>

      <div>
        <Label className={labelClass}>Photos illustratives <span className="text-xs text-zinc-500 font-normal">(facultatif — JPG/PNG/WEBP, 8 Mo max/photo)</span></Label>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-zinc-900 text-white px-4 py-2 rounded-sm text-sm hover:bg-zinc-800 transition-colors" data-testid="conformateur-photo-add-btn">
          <Upload size={16} />
          {uploading ? 'Envoi en cours...' : 'Ajouter des photos'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            multiple
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid="conformateur-photo-input"
          />
        </label>

        {(form.conformateurPhotoIds || []).length > 0 && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="conformateur-photo-grid">
            {form.conformateurPhotoIds.map((fid) => (
              <div key={fid} className="relative bg-zinc-100 border border-zinc-200 rounded-sm overflow-hidden group aspect-square">
                {previews[fid] ? (
                  <img src={previews[fid]} alt="Conformateur" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-zinc-400">Chargement…</div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(fid)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Retirer la photo"
                  data-testid={`conformateur-photo-remove-${fid}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
